import { createFileRoute, notFound, redirect, useNavigate } from "@tanstack/react-router"
import { useCallback, useState } from "react"
import type { TranscriptResponse } from "../../utils/conversation-store.ts"
import ChatInterface from "../components/ChatInterface"
import { API_BASE_URL } from "../config"
import { usePendingMessages } from "../contexts/PendingMessageContext.tsx"
import { transcriptToChatMessages } from "../utils/chat-state.ts"
import { toast } from "../utils/toast.tsx"

export const Route = createFileRoute("/conversations/view/$conversationId")({
  shouldReload: false,
  beforeLoad: ({ search }) => {
    if (!search.agent) {
      throw redirect({ to: "/new", search, replace: true })
    }
  },
  loader: async ({ params }) => {
    const res = await fetch(`${API_BASE_URL}/api/conversations/${params.conversationId}/transcript`, {
      credentials: "include"
    })
    if (!res.ok) {
      if (res.status === 404) {
        throw notFound({ routeId: "__root__" })
      }
      throw new Error("Failed to load transcript")
    }
    const data = (await res.json()) as TranscriptResponse
    return { transcript: data.transcript }
  },
  component: ViewConversationPage
})

function ViewConversationPage() {
  const { transcript } = Route.useLoaderData()
  const [input, setInput] = useState("")
  const { devTools: devtoolsParam, agent } = Route.useSearch({ structuralSharing: true })
  const navigate = useNavigate({ from: "/conversations/view/$conversationId" })
  const { setMessages } = usePendingMessages()

  const messages = transcriptToChatMessages(transcript)

  const handleSend = useCallback(async () => {
    if (!input.trim() || !agent) return

    try {
      const response = await fetch(`${API_BASE_URL}/api/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          agentName: agent,
          transcript
        })
      })

      if (!response.ok) {
        toast.error("Failed to fork conversation", {
          description: `POST /api/conversations returned ${response.status} for agent "${agent}" with ${transcript.length} transcript entries`
        })
        return
      }

      const data = await response.json()
      const conversationId = data?.conversationId
      if (conversationId) {
        setMessages([{ text: input.trim() }])
        navigate({
          to: "/conversations/$conversationId",
          params: { conversationId },
          search: (prev) => prev
        })
      }
    } catch (error) {
      toast.error("Failed to fork conversation", {
        description: `POST /api/conversations threw for agent "${agent}": ${error instanceof Error ? error.message : "Network error"}`
      })
    }
  }, [input, agent, transcript, navigate, setMessages])

  const handleCloseDevTools = useCallback(() => {
    navigate({
      to: ".",
      search: (prev) => ({ ...prev, devTools: undefined })
    })
  }, [navigate])

  return (
    <ChatInterface
      input={input}
      setInput={setInput}
      flowState={{}}
      messages={messages}
      currentResponse=""
      onSend={handleSend}
      stepPath={[]}
      stepHistory={[]}
      onSubmitClarification={() => {}}
      onCloseClarification={() => {}}
      currentThought={null}
      devToolsMode={devtoolsParam}
      onCloseDevTools={handleCloseDevTools}
    />
  )
}
